import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useSearchParams, useLocation } from "react-router-dom";
import { PageContainer } from "../components/Container/PageContainer";
import { getCollection } from "../utils/pureDataFetch";
import { Stack, Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import CollectionTitleSection from "../components/CollectionTitleSection";
import CollectionContentOfTable from "../components/CollectionContentOfTable";
import CollectionThreadContent from "../components/CollectionThreadContent";
import { Typography } from "@mui/material";
import { IconArrowLeft } from "@tabler/icons-react";
import UpdateCollectionModal from "../components/modal/UpdateCollectionModal";
import CollectionLoginPromptModal from "../components/modal/CollectionLoginPromptModal";
import { updateCollection, deleteCollection } from "../utils/pureDataFetch";
import { useNavigate } from "react-router-dom";
import { CollectionKeyMetricSummarySection } from "../template/metric/CollectionKeyMetricSummarySection";
import colorsCustom from "../designTokens/colorsCustom";
import { useThreadsState } from "../contexts/ThreadsContext";
import { useUser } from "../contexts/UserContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { processThreadMediaWithProxy } from "../utils/mediaDownloader";
import layout from "../designTokens/layout";
import {
	buildCollectionFallbackPath,
	rememberCollectionEntryPath,
	hasCollectionPromptSeen,
	markCollectionPromptSeen,
	clearCollectionSessionState,
} from "../utils/collectionLogin";

const CollectionPage = ({ isMe }) => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { collectionId } = useParams();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const threadID = searchParams.get("threadID");
	const isMobile = useIsMobile("xl");
	const [collection, setCollection] = useState(null);
	const contentTopRef = useRef(null);
	const [shouldScrollToTitle, setShouldScrollToTitle] = useState(false);
	const [selectedThread, setSelectedThread] = useState(null);
	const [isVisible, setIsVisible] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [selectedThreads, setSelectedThreads] = useState([]);
	const [collectionList, setCollectionList] = useState([]);
	const [metrics, setMetrics] = useState({
		views: 0,
		likes: 0,
		replies: 0,
		reposts: 0,
	});
	const { lifetimeThreadsList } = useThreadsState();
	const { isLogin } = useUser();
	const [loginPromptOpen, setLoginPromptOpen] = useState(false);

	useEffect(() => {
		if (isLogin) {
			setLoginPromptOpen(false);
			clearCollectionSessionState();
			return;
		}

		const currentPath = buildCollectionFallbackPath();

		rememberCollectionEntryPath(currentPath);
		setLoginPromptOpen(!hasCollectionPromptSeen());
	}, [
		isLogin,
		location.pathname,
		location.search,
		location.hash,
	]);

	const handleLoginPromptClose = () => {
		markCollectionPromptSeen();
		setLoginPromptOpen(false);
	};

	// 미디어 정보를 처리하는 함수
	const processThreadWithMedia = (thread) => {
		// 🔍 children 구조 분석 - Threads API는 {data: []} 형태
		let childrenArray = [];
		if (
			thread.children &&
			thread.children.data &&
			Array.isArray(thread.children.data)
		) {
			childrenArray = thread.children.data;
		}

		const hasMedia = !!(thread.media_url || childrenArray.length > 0);
		const mediaUrls = [];

		if (childrenArray.length > 0) {
			// 캐러셀/다중 미디어
			childrenArray.forEach((child, index) => {
				if (child.media_url) {
					mediaUrls.push({
						url: child.media_url,
						type: child.media_type,
						alt: child.alt_text || "",
					});
				}
			});
		} else if (thread.media_url) {
			// 단일 미디어
			mediaUrls.push({
				url: thread.media_url,
				type: thread.media_type,
				alt: thread.alt_text || "",
			});
		}

		const processedThread = {
			...thread,
			hasMedia,
			mediaType: thread.media_type,
			mediaUrls,
			isCarousel: !!(childrenArray.length > 1),
		};

		return processedThread;
	};

	useEffect(() => {
		getCollection(collectionId).then(async (collection) => {
			// 컬렉션의 각 스레드에 미디어 정보 추가 및 날짜순 정렬
			const threadsWithMedia = collection.threadList
				.map(processThreadWithMedia)
				.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)); // 오래된 것부터 정렬

			// 🔥 미디어 처리 (Firebase Functions 프록시 사용)
			const threadsWithDownloadedMedia = threadsWithMedia.map((thread) => {
				if (thread.hasMedia) {
					try {
						return processThreadMediaWithProxy(thread);
					} catch (error) {
						console.error(
							`❌ Failed to process proxy for thread ${thread.id}:`,
							error
						);
						// 프록시 처리 실패 시 원본 스레드 반환
						return thread;
					}
				}
				return thread;
			});

			// 🚀 모든 이미지를 미리 preload하여 완전히 캐시
			const preloadPromises = threadsWithDownloadedMedia
				.filter((thread) => thread.hasMedia && thread.mediaUrls)
				.flatMap((thread) =>
					thread.mediaUrls
						.filter((media) => media.type !== "VIDEO") // 비디오 제외
						.map((media) => {
							return new Promise((resolve) => {
								const img = new Image();
								img.onload = () => resolve();
								img.onerror = () => resolve(); // 실패해도 계속 진행
								img.src = media.url;
							});
						})
				);

			// 모든 이미지 preload 완료 후 컬렉션 설정
			Promise.all(preloadPromises).then(() => {
				console.log(
					`🎉 All images preloaded: ${preloadPromises.length} images`
				);
			});

			const processedCollection = {
				...collection,
				threadList: threadsWithDownloadedMedia,
			};

			setCollection(processedCollection);

			// For update collection
			setTitle(collection.title);
			setDescription(collection.description);
			setSelectedThreads(collection.threadList.map((thread) => thread.id));
			setCollectionList(collection.threadList);

			// Calculate total metrics
			const totalMetrics = processedCollection.threadList.reduce(
				(acc, thread) => {
					acc.views += thread.views || 0;
					acc.likes += thread.likes || 0;
					acc.replies += thread.replies || 0;
					acc.reposts += thread.reposts || 0;
					return acc;
				},
				{ views: 0, likes: 0, replies: 0, reposts: 0 }
			);

			setMetrics(totalMetrics);
		});
	}, [collectionId]);

	// threadID 변경시 selectedThread만 업데이트 (컬렉션 재로드 없이)
	useEffect(() => {
		if (!collection) return;
		if (threadID) {
			const foundThread = collection.threadList.find(
				(thread) => thread.id === threadID
			);
			setSelectedThread(foundThread || collection.threadList[0]);
		} else {
			setSelectedThread(collection.threadList[0]);
		}
	}, [threadID, collection]);

	// 모바일: 목차 클릭으로 선택된 스레드 변경 시에만 스크롤 조정
	useEffect(() => {
		if (!isMobile) return;
		if (!selectedThread) return;
		if (!shouldScrollToTitle) return; // 목차 클릭 시에만 동작
		const headerOffset = 80; // AppBar 보정값
		const target = contentTopRef.current;
		if (target) {
			const top =
				target.getBoundingClientRect().top + window.scrollY - headerOffset;
			window.scrollTo({ top, behavior: "smooth" });
		}
		// 한 번 스크롤 후 플래그 해제
		setShouldScrollToTitle(false);
	}, [selectedThread, isMobile, shouldScrollToTitle]);

	// 목차에서 선택 시에만 스크롤 트리거
	const setSelectedThreadFromToc = (...args) => {
		setShouldScrollToTitle(true);
		setSelectedThread(...args);
	};

	// 로그인하지 않은 경우 미디어 표시를 숨기기 위해 선택된 스레드의 미디어 정보를 제거
	const selectedThreadForView = useMemo(() => {
		if (!selectedThread) return null;
		if (isLogin) return selectedThread;
		return {
			...selectedThread,
			hasMedia: false,
			mediaUrls: [],
		};
	}, [selectedThread, isLogin]);

	const handleThreadSelect = (threadId) => {
		setSelectedThreads((prev) =>
			prev.includes(threadId)
				? prev.filter((id) => id !== threadId)
				: [...prev, threadId]
		);
	};

	useEffect(() => {
		if (isMe && modalOpen) {
			setCollectionList(() => {
				console.log(selectedThreads);
				return lifetimeThreadsList
					.filter((thread) => selectedThreads.includes(thread.id))
					.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)); // 오래된 것부터 정렬
			});
		}
	}, [selectedThreads, modalOpen, isMe, lifetimeThreadsList]);

	const handleRemoveThread = (threadId) => {
		setSelectedThreads((prev) => prev.filter((id) => id !== threadId));
		setCollectionList((prev) =>
			prev.filter((thread) => thread.id !== threadId)
		);
	};

	const handleCreateCollection = async (collectionData) => {
		try {
			const newCollection = await updateCollection(
				collectionId,
				collectionData
			);
			console.log("newCollection", newCollection);
			setCollection(newCollection);
			setModalOpen(false); // Close the modal after creation
		} catch (error) {
			console.error("Error creating collection:", error);
		}
	};

	const handleDeleteCollection = async () => {
		console.log("collectionId", collectionId);
		await deleteCollection(collectionId);
		alert("Collection deleted successfully!");
		navigate("/my-collection");
	};

	// 컬렉션이 비어있는지 확인
	const isEmptyCollection =
		collection &&
		(!collection.threadList || collection.threadList.length === 0);

	return (
		<PageContainer>
			{collection && (
				<Stack
					spacing={isMobile ? 6 : 12}
					sx={{
						width: "100%",
						px: isMobile ? 1 : 0, // 모바일에서 좌우 패딩 추가
					}}
				>
					{/* 모바일 전용: 상단 백 버튼 */}

					<Box
						sx={{
							width: "100%",
							display: "flex",
							alignItems: "center",
							ml: -1,
							mr: -1,
						}}
					>
						<Button
							variant="text"
							color="primary"
							size="large"
						onClick={() => navigate(-1)}
							startIcon={<IconArrowLeft size={22} />}
							sx={{
								textTransform: "none",
								p: 0,
							}}
						>
						돌아가기
						</Button>
					</Box>

					{/* 메트릭 섹션 - 내 컬렉션일 경우 최상단에 표시 */}
					{isMe && !isEmptyCollection && (
						<Box
							sx={{
								width: "100%",
								display: "flex",
								justifyContent: "flex-start",
								padding: isMobile ? "0" : "0 24px 0 0",
							}}
						>
							<Box
								sx={{
									width: isMobile ? layout.content.maxWidth.mobile : "100%",
									maxWidth: "100%",
								}}
							>
								<CollectionKeyMetricSummarySection
									isXL={isMobile}
									isSM={isMobile}
									metrics={metrics}
									views={metrics.views}
									likes={metrics.likes}
									replies={metrics.replies}
									reposts={metrics.reposts}
									numOfThreads={collection.threadList.length}
								/>
							</Box>
						</Box>
					)}

					{/* 타이틀 섹션 - 항상 표시 */}
					<CollectionTitleSection
						isMobile={isMobile}
						collection={collection}
						setModalOpen={setModalOpen}
						handleDeleteCollection={handleDeleteCollection}
						isMe={isMe}
					/>

					{/* 타이틀 하단 구분선 */}
					<Box
						sx={{
							width: "100%",
							height: "1px",
							backgroundColor: colorsCustom.ui.dark.subtle,
							my: 1,
						}}
					/>

					{/* 컬렉션이 비어있지 않은 경우에만 콘텐츠 표시 */}
					{!isEmptyCollection ? (
						<>
							{/* 메인 콘텐츠 영역 - 반응형 레이아웃 */}
							<Stack
								direction={isMobile ? "column" : "row"}
								spacing={0}
								sx={{
									width: "100%",
									alignItems: "flex-start",
									minHeight: isMobile ? "calc(100vh - 200px)" : "auto", // 모바일에서 적절한 높이 확보
								}}
							>
								{selectedThreadForView && (
									<Box ref={contentTopRef} sx={{ width: "100%" }}>
										<CollectionThreadContent
											selectedThread={selectedThreadForView}
											setSelectedThread={setSelectedThread}
											threadList={collection.threadList}
											isMobile={isMobile}
											collectionID={collectionId}
											isVisible={isVisible}
											setIsVisible={setIsVisible}
											collection={collection}
									isMe={isMe}
									isLogin={isLogin}
										/>
									</Box>
								)}

								{/* 데스크톱에서만 목차 표시, 모바일에서는 오버레이로 */}
								{!isMobile && (
									<CollectionContentOfTable
										collection={collection}
										selectedThread={selectedThread}
										setSelectedThread={setSelectedThreadFromToc}
										isMobile={isMobile}
										t={t}
										isVisible={isVisible}
										setIsVisible={setIsVisible}
									/>
								)}

								{/* 모바일 오버레이 목차 */}
								{isMobile && (
									<Box sx={{ position: "relative", zIndex: 1500 }}>
										<CollectionContentOfTable
											collection={collection}
											selectedThread={selectedThread}
											setSelectedThread={setSelectedThreadFromToc}
											isMobile={isMobile}
											t={t}
											isVisible={isVisible}
											setIsVisible={setIsVisible}
										/>
									</Box>
								)}
							</Stack>
						</>
					) : (
						/* 빈 컬렉션 안내 메시지 */
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								minHeight: "40vh",
								textAlign: "center",
								gap: 2,
								px: 2,
							}}
						>
							<Typography
								variant="h4"
								sx={{
									fontWeight: 600,
									color: "text.primary",
									mb: 2,
								}}
							>
								{t("collectionPage.emptyCollection.title")}
							</Typography>
							<Typography
								variant="body1"
								sx={{
									color: "text.secondary",
									mb: 1,
								}}
							>
								{t("collectionPage.emptyCollection.description")}
							</Typography>
							{isMe && (
								<>
									<Typography
										variant="body2"
										sx={{
											color: "text.secondary",
											mb: 3,
										}}
									>
										{t("collectionPage.emptyCollection.action")}
									</Typography>
									<Button
										variant="contained"
										onClick={() => setModalOpen(true)}
										sx={{
											borderRadius: 2,
											px: 4,
											py: 1.5,
										}}
									>
										{t("myCollection.modify")}
									</Button>
								</>
							)}
						</Box>
					)}
				</Stack>
			)}
			{modalOpen && (
				<UpdateCollectionModal
					modalOpen={modalOpen}
					setModalOpen={setModalOpen}
					title={title}
					setTitle={setTitle}
					description={description}
					setDescription={setDescription}
					threadsList={lifetimeThreadsList}
					selectedThreads={selectedThreads}
					handleThreadSelect={handleThreadSelect}
					isMobile={isMobile}
					onUpdateCollection={handleCreateCollection}
					collectionList={collectionList}
					handleRemoveThread={handleRemoveThread}
					isNew={false}
					initialIsPublic={collection?.isPublic ?? true}
				/>
			)}
			<CollectionLoginPromptModal
				open={loginPromptOpen}
				onClose={handleLoginPromptClose}
				isMobile={isMobile}
			/>
		</PageContainer>
	);
};

export default CollectionPage;
